import { formatPhoneNumber } from 'react-phone-number-input';

import TransactionsAPI from '@api/TransactionsAPI';
import axios from 'axios';
import { isEmpty } from 'lodash';

import {
    GetUserAuditHistoryPathParams,
    GetUserAuditHistoryResponse,
    GetUserPathParams,
    GetUserResponseBody,
    IPublicAccount,
    IPublicUser,
    ListUsersByIdRequestBody,
    ListUsersByIdResponseBody,
    ListUsersQueryParams,
    ListUsersResponseBody,
    UserRole,
    UserTransaction,
} from '@sparkplug/lib';

import { fetchGroup } from '@core/accounts';

import { useAdvancedQuery, useQueries } from '@hooks/QueryHooks';

import { fetchBatchedData } from '@helpers/api';
import { sortByString } from '@helpers/ui';

import { AccountUsersAndProfiles, IAccountUser } from '@app/types/UsersTypes';

import { formatLastTransactionData } from '../utils';

const API = {
    getUserById: async ({ userId }: GetUserPathParams): Promise<GetUserResponseBody> => {
        return (await axios.get<GetUserResponseBody>(`/api/v1/users/${userId}`)).data;
    },
    getUserAuditHistory: async ({
        userId,
    }: GetUserAuditHistoryPathParams): Promise<GetUserAuditHistoryResponse> => {
        return (
            await axios.get<GetUserAuditHistoryResponse>(`/api/v1/users/${userId}/audit-history`)
        ).data;
    },
    listUsers: async (params: ListUsersQueryParams): Promise<ListUsersResponseBody> => {
        return (await axios.get<ListUsersResponseBody>('/api/v1/users', { params })).data;
    },
    listUsersById: async (body: ListUsersByIdRequestBody): Promise<ListUsersByIdResponseBody> => {
        return (await axios.post<ListUsersByIdResponseBody>('/api/v1/users/multi/get', body)).data;
    },
    listCCUsers: async (params: ListUsersQueryParams): Promise<ListUsersResponseBody> => {
        return (await axios.get<ListUsersResponseBody>('/api/v1/users/cc', { params })).data;
    },
    getAllAccountUsers: async ({
        groupId,
        includeDisabledLocations,
    }: {
        groupId: string;
        includeDisabledLocations: boolean;
    }) => {
        return (
            await axios.get(
                `/api/v1/sparkplug/users?group_id=${groupId}&include_disabled_locations=${includeDisabledLocations}`,
            )
        ).data;
    },
};

const fetchUsers = async ({ posUserId, role }: { posUserId?: string; role?: UserRole }) => {
    return fetchBatchedData((offset, limit) => {
        return API.listUsers({ offset, limit, role, pos_employee_profile_id: posUserId });
    });
};

const getAllSparkplugUsers = async (queryParameters?: {
    posUserId?: string;
    role?: UserRole;
}): Promise<(IPublicUser & { phoneNumberFormatted: string; key: string })[]> => {
    const { data } = await fetchUsers(queryParameters ?? {});

    return data
        .map((user) => {
            const phoneNumberFormatted =
                user?.phoneNumber != null && user?.phoneNumber.length > 0
                    ? formatPhoneNumber(user?.phoneNumber)
                    : '--';

            return {
                ...user,
                key: user._id,
                phoneNumberFormatted,
            };
        })
        .sort(sortByString('firstName', 'asc'));
};

const getUsers = async (
    params: ListUsersQueryParams,
): Promise<{
    data: IPublicUser[];
    meta: { total: number; offset: number; limit: number };
}> => {
    const { data, meta } = await API.listCCUsers(params);
    return { data, meta };
};

/**
 * export query key for invaliding mutations using the `updateQuery` option
 * with a `useAdvancedMutation` in UserMutations
 *
 * Example from `useSaveUserProfileMutation` in `UserMutations.tsx`:
 *
 * `{ updateQuery: { queryKey: getSparkplugUsersQueryKey() } }
 */
export const getSparkplugUsersQueryKey = (queryParameters?: {
    posUserId?: string;
    role?: UserRole;
}) => (queryParameters ? ['sparkplugUsers', JSON.stringify(queryParameters)] : ['sparkplugUsers']);

export const useSparkplugUsers = (queryParameters?: { posUserId?: string; role?: UserRole }) => {
    const {
        isFetched: isSparkplugUsersReady,
        data: sparkplugUsers,
        refetch: refetchSparkplugUsers,
    } = useAdvancedQuery(getSparkplugUsersQueryKey(queryParameters), () =>
        getAllSparkplugUsers(queryParameters),
    );

    return {
        isSparkplugUsersReady,
        sparkplugUsers,
        refetchSparkplugUsers,
    };
};

const getUsersQueryKey = (listUsersQueryParams: ListUsersQueryParams) => [
    'users',
    JSON.stringify(listUsersQueryParams),
];

export const useCCUsers = (listUsersQueryParams: ListUsersQueryParams) => {
    const {
        isFetched: isUsersReady,
        data,
        refetch: refetchUsers,
    } = useAdvancedQuery(
        getUsersQueryKey(listUsersQueryParams),
        () => getUsers(listUsersQueryParams),
        {
            enabled: Boolean(listUsersQueryParams.limit),
        },
    );
    return {
        isUsersReady,
        users: data?.data,
        meta: data?.meta,
        refetchUsers,
    };
};

export const getSparkplugUserQueryKey = (userId: string) => ['sparkplugUsers', userId];
export const useSparkplugUser = (userId: string) => {
    const {
        isFetched: userIsReady,
        status: userLoadingStatus,
        data: user,
        refetch: refetchUser,
        error: userLoadingError,
    } = useAdvancedQuery(getSparkplugUserQueryKey(userId), () => API.getUserById({ userId }));

    return {
        userIsReady,
        user,
        refetchUser,
        userLoadingStatus,
        userLoadingError,
    };
};

export const fetchUsersById = async ({ userIds }: { userIds: string[] }) => {
    return API.listUsersById({ userIds });
};

export const useSparkplugUsersById = ({ userIds }: { userIds: string[] }) => {
    const queryResults = useQueries({
        queries: (userIds || []).map((userId) => ({
            queryKey: getSparkplugUserQueryKey(userId),
            queryFn: () => API.getUserById({ userId }),
        })),
    });

    return {
        usersAreReady: queryResults.every(({ isFetched }) => isFetched),
        users: queryResults.map(({ data }) => data),
        refetchUsers: Promise.all(queryResults.map(({ refetch }) => refetch)),
    };
};

/**
 * @deprecated Remove this export and refactor usage with `useSparkplugUser`
 */
export const getUserData = async (userId: string): Promise<IPublicUser & { fullName: string }> => {
    const user = await API.getUserById({ userId });

    return {
        ...user,
        fullName: `${user?.firstName} ${user?.lastName}`.trim(),
    };
};

export const getUserTransactionsQueryKey = (sparkId: string, flexibleEmployeeId: string) => [
    'userTransactions',
    sparkId,
    flexibleEmployeeId,
];

export const useUserTransactionsQuery = (sparkId: string, flexibleEmployeeId: string) => {
    const {
        data,
        isFetched: transactionsAreReady,
        refetch: refetchTransactions,
    } = useAdvancedQuery<{ transactions: UserTransaction[] }, Error>(
        getUserTransactionsQueryKey(sparkId, flexibleEmployeeId),
        async () => {
            const response = await TransactionsAPI.getSparkTransactionsByFlexibleEmployeeId({
                sparkId,
                flexibleEmployeeId,
            });
            return { transactions: response.transactions };
        },
        {
            enabled: Boolean(sparkId && flexibleEmployeeId),
        },
    );

    return {
        transactions: data?.transactions ?? [],
        transactionsAreReady,
        refetchTransactions,
    };
};

/**
 * @deprecated Remove this export and refactor usage with `useAccountUsersQuery`
 */
export const getAccountUsers = async ({
    groupId,
    showDisabledLocations = false,
}: {
    groupId?: string;
    showDisabledLocations?: boolean;
}): Promise<IAccountUser[]> => {
    if (!groupId) {
        return [];
    }

    const { usersInGroup, unattachedPosEmployeeProfiles } = (await API.getAllAccountUsers({
        groupId,
        includeDisabledLocations: showDisabledLocations,
    })) as AccountUsersAndProfiles;

    return [...usersInGroup, ...unattachedPosEmployeeProfiles]
        .map((user) => {
            const { lastTransactionDaysAgo, lastTransactionDaysAgoStr } =
                formatLastTransactionData(user);

            // Reorder location names so the primary location (most recent transaction) comes first
            const getReorderedLocationStr = (): string | undefined => {
                const { locationNames = [], locationIds = [], lastTransactionLocationId } = user;
                if (!locationNames.length) return undefined;

                // Find the index of the primary location (most recent transaction location)
                const primaryLocationIndex = lastTransactionLocationId
                    ? locationIds.findIndex(
                          (id) => String(id) === String(lastTransactionLocationId),
                      )
                    : -1;

                // If we found a valid primary location, reorder the array
                if (primaryLocationIndex > 0) {
                    const reordered = [...locationNames];
                    const [primaryLocation] = reordered.splice(primaryLocationIndex, 1);
                    reordered.unshift(primaryLocation);
                    return reordered.join(',');
                }

                // Default: return as-is (first location is already primary or no match found)
                return locationNames.join(',');
            };

            return {
                ...user,
                flexibleEmployeeId: user.userId ?? user.posEmployeeProfileIds?.[0],
                key: [user.userId, user.posEmployeeProfileIds.join('.')].join('|'),
                label: user.fullName,
                lastTransactionDaysAgo,
                lastTransactionDaysAgoStr,
                locationStr: getReorderedLocationStr(),
                phoneNumberFormatted: user.phoneNumber
                    ? formatPhoneNumber(user.phoneNumber) || undefined
                    : undefined,
                role: user.groupRole,
            };
        })
        .sort(sortByString('label', 'asc'));
};

/**
 * The `useAccountUsersQuery` implements the logic for fetching/getting/merging
 * all the associated pos/account/user data into the `IAccountUser` objects expectet
 * by the UI.
 *
 * NOTE: Ideally, all this logic should move to it's own endpoint on the backend. First,
 * for removing the load from the frontend, but even more importantly, brands have no
 * visibility into the active/inactive users for a retailer. This leads to issues with
 * inactive users showing in the UI for brands. An endpoint returning the correctly filtered
 * data for an retailer to a brand would greatly clean this up
 */
export const getAccountUsersQueryKey = (accountId: string, showDisabledLocations?: boolean) => [
    'accountUsers',
    accountId,
    showDisabledLocations ?? false,
];
export const useAccountUsersQuery = (
    accountId?: string,
    showDisabledLocations: boolean = false,
) => {
    const {
        data: accountUsers = [],
        isFetched: accountUsersAreReady,
        refetch: refetchAccountUsers,
    } = useAdvancedQuery(
        getAccountUsersQueryKey(accountId!, showDisabledLocations),
        () => getAccountUsers({ groupId: accountId, showDisabledLocations }),
        {
            enabled: !!accountId,
        },
    );

    return {
        accountUsers,
        accountUsersAreReady,
        refetchAccountUsers,
    };
};

const getSparkplugUsers = async (account?: IPublicAccount): Promise<IPublicUser[]> => {
    if (isEmpty(account?.members)) {
        return [];
    }

    const accountMembers = account?.members || [];
    const accountMemberIds = accountMembers.map((member) => member?.userId);

    const usersResponse = await fetchUsersById({ userIds: accountMemberIds });
    const users = usersResponse?.data || [];
    return users;
};

const getSparkplugUsersByAccountId = async (accountId?: string) => {
    if (accountId != null) {
        const group = await fetchGroup(accountId);
        return getSparkplugUsers(group);
    }

    return [];
};

export const useAccountSparkplugUsersQueryByGroupId = (accountId?: string) => {
    const { isLoading, data, refetch } = useAdvancedQuery(
        getAccountUsersQueryKey(accountId!),
        () => getSparkplugUsersByAccountId(accountId),
        { enabled: accountId != null },
    );

    return {
        accountSparkplugUsersAreReady: !isLoading,
        accountSparkplugUsers: data,
        refetchAccountSparkplugUsers: refetch,
    };
};

// User Audit History Hook
export const getUserAuditHistoryQueryKey = (userId: string) => ['userAuditHistory', userId];

export const useUserAuditHistory = (userId?: string) => {
    const {
        isFetched: isUserAuditHistoryReady,
        data: userAuditHistory,
        refetch: refetchUserAuditHistory,
        isLoading: isUserAuditHistoryLoading,
        error: userAuditHistoryError,
    } = useAdvancedQuery(
        getUserAuditHistoryQueryKey(userId!),
        () => API.getUserAuditHistory({ userId: userId! }),
        {
            enabled: Boolean(userId),
        },
    );

    return {
        isUserAuditHistoryReady,
        userAuditHistory,
        refetchUserAuditHistory,
        isUserAuditHistoryLoading,
        userAuditHistoryError,
    };
};
